import { useState } from "react";
import { ChevronDown } from "lucide-react";
import Footer from "@/components/Footer";
import Header from "@/components/Header";

const faqs = [
  {
    question: "What is Relocation Clarity?",
    answer: "Relocation Clarity provides independent relocation guidance for people considering life abroad. Rather than promoting destinations or relying on generic country rankings, we evaluate each option based on your budget, healthcare needs, residency options, lifestyle, and personal priorities—helping you choose the country that's right for your life."
  },
  {
    question: "Who is this for?",
    answer: "Relocation Clarity is for people who are seriously considering life abroad and want more than generic country rankings or conflicting online advice. If you're looking for an independent, structured way to compare realistic options before making a major life decision, Relocation Clarity was built for you."
  },
  {
    question: "How should I use this alongside my own research?",
    answer: "Use Relocation Clarity to get clear on what you want in a country, narrow the shortlist, and spot the tradeoffs that matter most. Even if you already have countries in mind, it can show whether they really fit your priorities or whether another option checks more of the right boxes. Then confirm important details through official government sources and qualified professionals as needed."
  },
  {
    question: "How did you choose the countries included in Relocation Clarity?",
    answer: "We intentionally focus on a carefully selected group of countries rather than trying to cover every destination in the world. Each country is included because it represents a realistic relocation option that can be evaluated consistently across the factors that matter most, including budget, healthcare, residency options, infrastructure, and lifestyle. Including every country isn't our goal. Helping you compare the strongest, most relevant options—and make a better relocation decision—is."
  },
  {
    question: "What if the country I’m considering isn’t one of my top results?",
    answer: "During the assessment, you can include countries you're especially interested in, and we'll show how they compare on your Results page. If one of those countries isn't among your top recommendations, it doesn't necessarily mean it's a bad choice. It means that, based on your priorities, budget, residency options, and other factors, other countries appear to be a stronger overall fit for your life today. You'll still be able to compare your selected country alongside your recommended results so you can understand the tradeoffs and make an informed decision."
  },
  {
    question: "Can I update my answers and refresh my plan?",
    answer: "Yes. It's common for your thinking to evolve as you learn more about living abroad. That's why every Relocation Blueprint includes one refresh at no additional cost. If your priorities, budget, or relocation plans change, simply update your assessment and generate a revised plan. If your plans change again later, you can always purchase a new Relocation Blueprint based on your updated answers."
  },
  {
    question: "Where does the information come from?",
    answer: "Relocation Clarity brings together publicly available information from government agencies, international organizations, and trusted country resources covering residency options, healthcare, cost of living, infrastructure, safety, and other practical relocation factors. We organize that information into a consistent, independent decision framework that helps you compare countries based on what matters most to you. Our goal isn't to rank countries from best to worst. It's to help you understand the tradeoffs so you can make a more informed relocation decision with confidence. Relocation Clarity provides planning guidance—not legal, tax, financial, or immigration advice."
  },
  {
    question: "Is this powered by AI?",
    answer: "Yes. Relocation Clarity uses AI to help organize information and present personalized guidance more efficiently. However, your recommendations are based on our structured evaluation methodology—not AI opinions alone. Our goal is to combine technology with a consistent, independent decision framework that helps you compare countries more clearly and make a more informed relocation decision. AI supports the experience. It doesn't replace independent methodology, professional advice, or your own judgment."
  },
  {
    question: "What does Relocation Clarity not do?",
    answer: "Relocation Clarity helps you make a more informed relocation decision, but it isn't a substitute for professional advice. We don't provide legal, tax, financial, or immigration advice, and we can't guarantee visa eligibility, approvals, or relocation outcomes. Our role is to help you compare the countries that best fit your priorities, understand the tradeoffs, and make a better-informed relocation decision before taking the next step."
  },
];

const AccordionItem = ({ question, answer, isOpen, onToggle, index }) => {
  return (
    <div className="border-b border-stone-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full py-5 flex items-center justify-between text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded-sm"
        aria-expanded={isOpen}
        data-testid={`faq-question-${index}`}
      >
        <span className="font-medium text-stone-800 text-base md:text-lg pr-4">
          {question}
        </span>
        <ChevronDown 
          className={`w-5 h-5 text-stone-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}
      >
        <p className="text-stone-600 leading-relaxed text-base">
          {answer}
        </p>
      </div>
    </div>
  );
};

const MethodologyFAQs = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Main Content */}
      <main className="py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Page Header */}
          <div className="mb-12">
            <h1 className="font-manrope font-bold text-3xl md:text-4xl text-foreground mb-3">
              Your Questions, Answered
            </h1>
            <p className="text-muted-foreground text-base">
              Learn how Relocation Clarity works, how recommendations are made, and how to use your results with confidence.
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="bg-white border border-stone-200 rounded-xl px-6 md:px-8" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                index={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onToggle={() => handleToggle(index)}
              />
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MethodologyFAQs;
